import { forwardRef, useEffect, useState, type JSX } from 'react';

import { fetchArtifactBlob } from '@/lib/api/quality.js';
import { cn } from '@/lib/utils.js';

type AuthBlobStatus = 'loading' | 'ready' | 'error';

/** Cap on the shared blob cache below, so a long session browsing many runs doesn't hold every
 *  artifact blob in memory forever. Oldest entry is evicted first — a simple bound, not a real LRU. */
const MAX_CACHED_BLOBS = 100;

/**
 * Module-level cache so concurrent or repeat mounts of the same artifact (e.g. the same screenshot
 * shown as a thumbnail and again expanded) fetch the binary once per session. Caches the in-flight
 * promise (not just the resolved blob) so simultaneous mounts dedupe too. A rejected fetch is
 * evicted immediately so a later mount can retry instead of being stuck with a cached failure.
 */
const blobCache = new Map<string, Promise<Blob>>();

/** Test-only escape hatch: the cache is module-level and outlives any single test, so a test that
 *  re-mocks the same artifact URL with a different outcome must clear it first. */
export function clearAuthBlobCacheForTests(): void {
  blobCache.clear();
}

function getCachedArtifactBlob(url: string): Promise<Blob> {
  const cached = blobCache.get(url);
  if (cached) return cached;
  const promise = fetchArtifactBlob(url).catch((error: unknown) => {
    blobCache.delete(url);
    throw error;
  });
  blobCache.set(url, promise);
  if (blobCache.size > MAX_CACHED_BLOBS) {
    const oldestKey = blobCache.keys().next().value;
    if (oldestKey !== undefined) blobCache.delete(oldestKey);
  }
  return promise;
}

/**
 * True once the observed node has entered (or come near) the viewport, then stays true — the
 * observer disconnects after the first hit since artifacts never need to "un-load". `rootMargin`
 * starts the fetch a little before the element is actually visible, so it's ready by the time the
 * user scrolls to it.
 */
function useIsNearViewport(): {
  setNode: (node: Element | null) => void;
  visible: boolean;
} {
  const [node, setNode] = useState<Element | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!node || visible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, visible]);

  return { setNode, visible };
}

/**
 * The /artifacts/* endpoint is bearer-authed, so a plain `<img src>`/`<video src>` (a browser
 * sub-request that can't carry the Authorization header) would 401. Fetch the binary through
 * `fetchArtifactBlob` (which attaches the platform JWT) as a Blob and expose it as an object URL
 * instead. Reports a distinct `status` so callers can tell "still loading" apart from "fetch
 * failed" and show an explicit failure state instead of a silent loading glyph.
 *
 * The fetch itself is gated on viewport proximity (`ref` must be attached to the rendered node) —
 * on a slow connection there's no reason to download every artifact on mount, only the ones the
 * user is about to see.
 */
function useAuthBlobUrl(
  url: string | null,
  options?: { cache?: boolean },
): {
  url: string | null;
  status: AuthBlobStatus;
  ref: (node: Element | null) => void;
} {
  const { setNode, visible } = useIsNearViewport();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthBlobStatus>('loading');
  const cache = options?.cache ?? true;

  useEffect(() => {
    // Reset on every `url` change so a stale 'error' from the previous src can't bleed through.
    setStatus('loading');
    setObjectUrl(null);
    if (!url || !visible) {
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    (cache ? getCachedArtifactBlob(url) : fetchArtifactBlob(url))
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [url, visible, cache]);

  return { url: objectUrl, status, ref: setNode };
}

/** Muted box shared by the loading glyph and the "unavailable" failure states. Forwards its ref so
 *  the placeholder itself — not just the eventual `<img>`/`<video>` — can be observed for viewport
 *  proximity; the placeholder is what's actually mounted while the fetch hasn't started yet. */
const MediaPlaceholder = forwardRef<
  HTMLDivElement,
  { label: string; className?: string; children: string }
>(function MediaPlaceholder({ label, className, children }, ref): JSX.Element {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center rounded-md border border-border bg-muted/40 text-xs text-muted-foreground',
        className,
      )}
      aria-label={label}
    >
      {children}
    </div>
  );
});

export function AuthImage({
  src,
  alt,
  className,
  onNaturalSize,
}: {
  src: string;
  alt: string;
  className?: string;
  /** The decoded image's own dimensions, once it loads. Used by callers that must shape a container
   *  around content whose real proportions they cannot know in advance (the player's unloaded frame,
   *  which otherwise guesses from the recording's kind). */
  onNaturalSize?: (size: { width: number; height: number }) => void;
}): JSX.Element {
  const { url, status, ref } = useAuthBlobUrl(src);

  useEffect(() => {
    if (status === 'error')
      console.warn(`AuthImage: screenshot unavailable — ${src}`);
  }, [status, src]);

  if (status === 'error') {
    // Don't reuse `alt` as the aria-label here — it would override the visible "Screenshot
    // unavailable" text for screen readers, announcing the original alt instead of the failure.
    return (
      <MediaPlaceholder
        ref={ref}
        label={`Screenshot unavailable: ${alt}`}
        className={className}
      >
        Screenshot unavailable
      </MediaPlaceholder>
    );
  }
  if (!url) {
    return (
      <MediaPlaceholder ref={ref} label={alt} className={className}>
        …
      </MediaPlaceholder>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className={className}
      loading="lazy"
      onLoad={(event) => {
        const image = event.currentTarget;
        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
          onNaturalSize?.({
            width: image.naturalWidth,
            height: image.naturalHeight,
          });
        }
      }}
    />
  );
}

/**
 * Always renders the `<video>` element — while loading, once ready, and even after a failed fetch —
 * so the forwardRef stays stable for the Video tab's timecode seeking. The blob src is filled in
 * once fetched; on a failed fetch the video is hidden and an explicit "Recording unavailable"
 * placeholder is layered on top instead of unmounting the element. The viewport-proximity ref is
 * attached to the wrapping div (not the `<video>` itself) so it doesn't collide with the caller's
 * forwarded ref.
 */
export const AuthVideo = forwardRef<
  HTMLVideoElement,
  { src: string; className?: string }
>(function AuthVideo({ src, className }, forwardedRef): JSX.Element {
  const { url, status, ref } = useAuthBlobUrl(src, { cache: false });

  useEffect(() => {
    if (status === 'error')
      console.warn(`AuthVideo: recording unavailable — ${src}`);
  }, [status, src]);

  // Always render the <video> element — even with no src, on a failed fetch — so the forwarded
  // ref stays stable. ExplorationReport/VideoReport hold onto this ref to scroll/seek the player;
  // if a fetch error unmounted the video instead, the ref would silently go null and those
  // controls would no-op even though the caller still thinks a recording is present. On error the
  // (src-less) video is hidden and the "Recording unavailable" placeholder is layered on top.
  return (
    <div className="relative" ref={ref}>
      <video
        ref={forwardedRef}
        src={url ?? undefined}
        controls={status !== 'error'}
        preload="metadata"
        className={cn('bg-black', className, status === 'error' && 'invisible')}
        // eslint-disable-next-line jsx-a11y/media-has-caption -- test recordings have no captions
      />
      {status === 'error' && (
        <MediaPlaceholder
          label="Recording unavailable"
          className={cn('absolute inset-0', className)}
        >
          Recording unavailable
        </MediaPlaceholder>
      )}
    </div>
  );
});
